import { useMemo } from 'react'
import * as React from 'react'
import gql from 'graphql-tag'
import update from 'immutability-helper'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import {
  NewsItemOrderField,
  OrderDirection,
  useGqlChannelSearchNewsListTabQuery,
} from 'generated/graphql'
import SearchResultHeader from '@app.components/SearchResultHeader'
import ScreenNewsItemFeedLoading from '@app.feature/channel/channel.newsItem/screen/ScreenNewsItemFeedLoading'
import NewsList from '@app.feature/channel/component/lists/NewsList'
import useIntersect from '@app.modules/hooks/useIntersect'

gql`
  query gqlChannelSearchNewsListTab(
    $filterBy: NewsItemFeedFilter
    $orderBy: NewsItemOrder
    $first: Int
    $after: String
  ) {
    newsItemFeed(
      filterBy: $filterBy
      orderBy: $orderBy
      first: $first
      after: $after
    ) {
      ...NewsItemFeedList_newsItemConnection
    }
  }
`

const ChannelSearchNewsList: React.FC = () => {
  const router = useRouter()
  const query = `${router.query.q || ``}`

  const variables = {
    filterBy: {
      q: query,
    },
    first: 20,
    orderBy: {
      direction: OrderDirection.DESC,
      field: NewsItemOrderField.CREATED_AT,
    },
  }

  const { data, loading, fetchMore } = useGqlChannelSearchNewsListTabQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const hasNextPage = useMemo(() => {
    if (!data || !data.newsItemFeed) return null
    return data.newsItemFeed.pageInfo.hasNextPage
  }, [data])

  const lastCursor = useMemo(() => {
    if (!data) return null
    return data.newsItemFeed.pageInfo.endCursor
  }, [data])

  const handleIntersect = () => {
    // validation
    if (!hasNextPage || !lastCursor || loading) return

    const variables = {
      filterBy: {
        q: query,
      },
      first: 20,
      after: lastCursor,
      orderBy: {
        direction: OrderDirection.DESC,
        field: NewsItemOrderField.CREATED_AT,
      },
    }

    fetchMore({
      variables: variables,
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.newsItemFeed)
          return previousQueryResult
        const result = update(previousQueryResult, {
          newsItemFeed: {
            edges: {
              $push: fetchMoreResult.newsItemFeed.edges,
            },
            pageInfo: {
              $set: fetchMoreResult.newsItemFeed.pageInfo,
            },
          },
        })
        return result
      },
    })
  }
  const target = useIntersect(handleIntersect, [lastCursor, loading])

  return (
    <StyledWrapper>
      <SearchResultHeader>
        소식{' '}
        {data?.newsItemFeed.totalCount
          ? `${data?.newsItemFeed.totalCount}건`
          : ``}
      </SearchResultHeader>
      <NewsList
        data={data?.newsItemFeed.edges || []}
        loading={loading}
        skeletonSize={3}
      />
      {hasNextPage && <div ref={target}></div>}
      {hasNextPage && <ScreenNewsItemFeedLoading />}
      {!loading && !hasNextPage && data?.newsItemFeed?.edges.length !== 0 && (
        <div className="end-content">모든 소식을 확인했습니다.</div>
      )}
    </StyledWrapper>
  )
}

export default ChannelSearchNewsList

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f5f6f8;
  gap: 10px;
  .end-content {
    display: flex;
    justify-content: center;
    padding: 20px;
    color: #999999;
  }
`
